<template>
    <div class="order-comments">
        <Spinner v-if="loading" />
        <h3 v-t="'comment.title'" class="order-comments__title" />

        <div class="order-comments__wrapper">
            <div class="order-comments__thread order-comments__thread--parent">
                <div
                    v-if="canCreate"
                    :class="[
                        'order-comments__results-form',
                        { 'is-limit-exceeded': content.length > contentLimit },
                    ]"
                >
                    <form @submit.prevent="sendParent">
                        <Wysiwyg
                            ref="parentEditorRes"
                            v-model="content"
                            :placeholder="$t('comment.placeholder')"
                        />
                        <div
                            v-if="content.length > contentLimit"
                            class="order-comments__results-warnmsg"
                        >
                            Exceeded the limit
                        </div>
                        <div class="order-comments__results-buttons">
                            <button
                                v-t="'misc.send'"
                                type="submit"
                                class="button button--medium order-comments__send"
                                :disabled="!isValidParent"
                            />
                            <button
                                v-if="orderCommented"
                                v-t="'misc.refresh'"
                                type="button"
                                class="button button--medium"
                                @click="refreshComments"
                            />
                        </div>
                    </form>
                </div>
                <PaginatedResource
                    ref="orderCommentsResource"
                    :fetch-method="fetchOrderCommentsParent"
                    @fetched="onParentFetched"
                >
                    <template #default="{ loading, resource }">
                        <div class="order-comments__results" @scroll="onScrollParent">
                            <Spinner v-if="loading" />
                            <div class="order-comments__results-items">
                                <div
                                    v-for="item in resource"
                                    :key="`fetchOrderCommentsParent${item.id}`"
                                >
                                    <div class="order-comments__results-header">
                                        <div
                                            class="order-comments__results-photo"
                                            :style="{
                                                'background-image': !!item.user.photo
                                                    ? `url(${item.user.photo})`
                                                    : 'none',
                                            }"
                                        />
                                        <div
                                            class="order-comments__results-user"
                                            v-text="
                                                `${item.user.first_name} ${item.user.last_name}`
                                            "
                                        />
                                        <button
                                            class="button order-comments__results-reply"
                                            @click.prevent="setActiveThread(item)"
                                        >
                                            <template v-if="!item.replies_count">
                                                {{ $t('misc.reply') }}
                                            </template>
                                            <template v-else-if="item.replies_count > 1">
                                                {{ item.replies_count }} {{ $t('misc.replies') }}
                                            </template>
                                            <template v-else>1 {{ $t('misc.reply') }}</template>
                                        </button>
                                        <div
                                            class="order-comments__results-date"
                                            v-text="
                                                formatLocaleTimezone(item.created, $i18n.locale)
                                            "
                                        />
                                    </div>
                                    <div
                                        class="order-comments__results-content"
                                        v-html="item.content"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </PaginatedResource>
            </div>
            <div v-if="activeThread" class="order-comments__thread order-comments__thread--childs">
                <button
                    class="button button--blank order-comments__thread-close"
                    @click.prevent="activeThread = undefined"
                >
                    <IconCross />
                </button>
                <div
                    v-if="showParentComment"
                    class="order-comments__results order-comments__results--empty"
                >
                    <div>
                        <div class="is-parent-content">
                            <div class="order-comments__results-header">
                                <div
                                    class="order-comments__results-photo"
                                    :style="{
                                        'background-image': !!activeThread.user.photo
                                            ? `url(${activeThread.user.photo})`
                                            : null,
                                    }"
                                />
                                <div
                                    class="order-comments__results-user"
                                    v-text="
                                        `${activeThread.user.first_name} ${activeThread.user.last_name}`
                                    "
                                />
                            </div>
                            <div
                                class="order-comments__results-date"
                                v-text="formatLocaleTimezone(activeThread.created, $i18n.locale)"
                            />
                            <div
                                class="order-comments__results-content"
                                v-html="activeThread.content"
                            />
                        </div>
                    </div>
                </div>
                <PaginatedResource
                    ref="orderCommentsThreadResource"
                    :fetch-method="fetchOrderCommentsChilds"
                    :page-size="childsPageSize"
                    delay-fetch
                    reversed
                    @fetched="onThreadFetched"
                >
                    <template #default="{ loading, resource: childs }">
                        <div class="order-comments__results" @scroll="onScrollThread">
                            <Spinner v-if="loading" />
                            <div class="order-comments__results-items">
                                <div
                                    v-for="child in childs"
                                    :key="`orderCommentsThreadResource${child.id}`"
                                    :class="{ 'is-parent-content': activeThread.id === child.id }"
                                >
                                    <div class="order-comments__results-header">
                                        <div
                                            class="order-comments__results-photo"
                                            :style="{
                                                'background-image': !!child.user.photo
                                                    ? `url(${child.user.photo})`
                                                    : null,
                                            }"
                                        />
                                        <div
                                            class="order-comments__results-user"
                                            v-text="
                                                `${child.user.first_name} ${child.user.last_name}`
                                            "
                                        />
                                    </div>
                                    <div
                                        class="order-comments__results-date"
                                        v-text="formatLocaleTimezone(child.created, $i18n.locale)"
                                    />
                                    <div
                                        class="order-comments__results-content"
                                        v-html="child.content"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </PaginatedResource>
                <div
                    v-if="canCreate"
                    :class="[
                        'order-comments__results-thread-form',
                        { 'is-limit-exceeded': threadContent.length > contentLimit },
                    ]"
                >
                    <form @submit.prevent="sendChild">
                        <Wysiwyg
                            ref="childEditorRes"
                            v-model="threadContent"
                            :placeholder="$t('misc.reply')"
                        />
                        <div
                            v-if="threadContent.length > contentLimit"
                            class="order-comments__results-warnmsg"
                        >
                            Exceeded the limit
                        </div>
                        <div class="order-comments__results-buttons">
                            <button
                                v-t="'misc.send'"
                                type="submit"
                                class="button button--medium order-comments__send"
                                :disabled="!isValidChild"
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { createOrderComment, fetchOrderComments } from '../../api/orders';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { formatLocaleTimezone } from '@speechki-frontend/shared/helpers/functions';
import { IconCross } from '@speechki-frontend/shared/ui/icons';
import Wysiwyg from '@speechki-frontend/shared/ui/Wysiwyg';

const convertHtmlToText = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

export default {
    name: 'OrderComments',
    components: {
        PaginatedResource,
        IconCross,
        Wysiwyg,
    },
    inject: ['registerWsHandler', 'removeWsHandler'],
    props: {
        orderId: {
            type: [Number, String],
            required: true,
        },
        canCreate: {
            type: Boolean,
            default: false,
        },
        childsPageSize: {
            type: Number,
            default: 10,
        },
        contentLimit: {
            type: Number,
            default: 2000,
        },
    },
    data() {
        return {
            loading: false,
            content: '',
            threadContent: '',
            activeThread: undefined,
            threadPage: 1,
            threadScrollTop: undefined,
            threadLoaded: false,
            replyId: undefined,
            orderCommented: false,
            showParentComment: true,
        };
    },
    computed: {
        isValidParent() {
            const text = convertHtmlToText(this.content);
            return text.trim().length && this.content.length <= this.contentLimit;
        },
        isValidChild() {
            const text = convertHtmlToText(this.threadContent);
            return text.trim().length && this.threadContent.length <= this.contentLimit;
        },
    },
    mounted() {
        this.registerWsHandler('order_commented', this.onOrderCommented);
    },
    beforeUnmount() {
        this.removeWsHandler('order_commented', this.onOrderCommented);
    },
    methods: {
        formatLocaleTimezone,
        fetchOrderCommentsParent(params = {}) {
            const { orderId } = this;
            return fetchOrderComments(orderId, null, params);
        },
        fetchOrderCommentsChilds(params = {}) {
            const { orderId } = this;
            const { thread_link: link = null } = this.activeThread || {};
            if (link) {
                return fetchOrderComments(null, link, params);
            }

            params.thread = this.activeThread.thread;
            params.page = 1;

            return fetchOrderComments(orderId, null, params);
        },
        async sendParent() {
            const res = this.$refs.orderCommentsResource;
            this.loading = true;
            try {
                let { content } = this;
                const data = { content };

                await createOrderComment(this.orderId, data);
                res.page = 1;
                await res.reloadPages();
                this.content = '';
                this.$refs.parentEditorRes.clear();
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
        async sendChild() {
            const res = this.$refs.orderCommentsThreadResource;
            const parentRes = this.$refs.orderCommentsResource;
            this.loading = true;

            try {
                const { threadContent: content } = this;
                const parent = this.activeThread.id;
                const parentItem = parentRes.resource.find(({ id }) => id === parent);
                const data = { content, parent };

                await createOrderComment(this.orderId, data);
                await this.$nextTick();

                this.threadContent = '';
                parentItem.replies_count++;
                this.$refs.childEditorRes.clear();

                await res.fetch();
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
        async setActiveThread(thread) {
            this.activeThread = undefined;
            await this.$nextTick();
            this.activeThread = thread;
            this.showParentComment = true;

            if (!thread.replies_count) return false;

            setTimeout(() => {
                const res = this.$refs.orderCommentsThreadResource;
                res.fetch();
            }, 0);
        },
        onScrollParent() {
            const res = this.$refs.orderCommentsResource;
            if (res.loading) return false;

            const target = res.$el;
            const { scrollTop, clientHeight, scrollHeight } = target;

            if (scrollTop + clientHeight >= scrollHeight) {
                if (res.canLoadMore) {
                    res.loadMore();
                }
            }
        },
        async onScrollThread() {
            await this.$nextTick();

            const res = this.$refs.orderCommentsThreadResource;
            if (res.loading) return false;

            const target = res.$el;
            const { scrollTop, clientHeight, scrollHeight } = target;

            setTimeout(() => {
                if (scrollTop === 0 && scrollHeight > clientHeight) {
                    if (res.canLoadMore) {
                        res.loadMore();
                    }
                    this.threadScrollTop = scrollTop;
                }
            }, 100);
        },
        onParentFetched() {
            this.orderCommented = false;
        },
        async onThreadFetched() {
            await this.$nextTick();
            const res = this.$refs.orderCommentsThreadResource;
            const { $el } = res;

            if (!res.isEmpty) {
                this.showParentComment = false;
            }

            setTimeout(() => {
                $el.scrollTop = res.page > 1 ? $el.clientHeight : $el.scrollHeight;
            }, 0);
        },
        onOrderCommented({ code, errors }) {
            if (errors) {
                console.log(errors);
                return;
            }

            if (code === 200) {
                this.orderCommented = true;
            }
        },
        refreshComments() {
            const res = this.$refs.orderCommentsResource;
            this.activeThread = undefined;
            res.page = 1;
            res.reloadPages();
        },
    },
};
</script>

<style lang="scss" src="./OrderComments.scss"></style>
